import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import { computed } from '@preact/signals-core';
import { html } from 'lit/static-html.js';

import { popTagSelect } from '../../core/component/tags/multi-tag-select.js';
import type { SelectTag } from '../../core/index.js';
import { BaseCellRenderer } from '../../core/property/index.js';
import { createFromBaseCellRenderer } from '../../core/property/renderer.js';
import { stopPropagation } from '../../core/utils/event.js';
import { createIcon } from '../../core/utils/uni-icon.js';
import { selectStyle } from './cell-renderer-css.js';
import {
  type SelectPropertyData,
  selectPropertyModelConfig,
} from './define.js';

export class SelectCell extends BaseCellRenderer<
  string,
  string,
  SelectPropertyData
> {
  closePopup?: () => void;
  /**
   * Set by the keyboard start-edit path (see `handleCharStartEdit`) so
   * the popover can pre-fill its search input with the character that
   * triggered it. Cleared on consume to avoid stale state when the
   * popover is re-opened via click.
   */
  pendingInitialText?: string;
  private readonly popTagSelect = (initialText?: string) => {
    this.closePopup = popTagSelect(popupTargetFromElement(this), {
      name: this.cell.property.name$.value,
      mode: 'single',
      options: this.options$,
      onOptionsChange: this._onOptionsChange,
      value: this._value$,
      onChange: v => {
        this.valueSetImmediate(v[0]);
      },
      onComplete: this._editComplete,
      minWidth: 400,
      initialText,
    });
  };

  _editComplete = () => {
    this.selectCurrentCell(false);
  };

  _onOptionsChange = (options: SelectTag[]) => {
    this.property.dataUpdate(data => {
      return {
        ...data,
        options,
      };
    });
  };

  options$ = computed(() => {
    const options = this.property.data$.value.options;
    return Array.isArray(options) ? options : [];
  });

  _value$ = computed(() => {
    const value = this.value;
    return value ? [value] : [];
  });

  override afterEnterEditingMode() {
    if (!this.closePopup) {
      const initialText = this.pendingInitialText;
      this.pendingInitialText = undefined;
      this.popTagSelect(initialText);
    }
  }

  override beforeExitEditingMode() {
    requestAnimationFrame(() => {
      this.closePopup?.();
      this.closePopup = undefined;
    });
  }

  override render() {
    return html`
      <div
        class="${selectStyle}"
        @pointerdown="${this.isEditing$.value ? stopPropagation : undefined}"
      >
        <affine-multi-tag-view
          .value="${this._value$.value}"
          .options="${this.options$.value}"
        ></affine-multi-tag-view>
      </div>
    `;
  }
}

export const selectPropertyConfig =
  selectPropertyModelConfig.createPropertyMeta({
    icon: createIcon('SingleSelectIcon'),
    cellRenderer: {
      view: createFromBaseCellRenderer(SelectCell),
    },
  });
